'use client'

import { main as legacyTheme } from '@caterdesk/ui--theme'
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded'
import ErrorRounded from '@mui/icons-material/ErrorRounded'
import EventRounded from '@mui/icons-material/EventRounded'
import InfoRounded from '@mui/icons-material/InfoRounded'
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded'
import WarningRounded from '@mui/icons-material/WarningRounded'
import {
  accordionClasses,
  alpha,
  iconButtonClasses,
  inputBaseClasses,
  stepIconClasses,
  stepLabelClasses,
  tablePaginationClasses,
  toggleButtonClasses,
} from '@mui/material'
import {
  createTheme,
  type Components,
  type PaletteColor,
  type PaletteOptions,
  type Theme,
  type ThemeOptions,
} from '@mui/material/styles'

import React from 'react'

const PRIMARY = { main: '#61C685', contrastText: '#FFF' }
const SECONDARY = { main: '#3F4144', contrastText: '#FFF' }

const DEFAULT_PALETTE: PaletteOptions = {
  primary: PRIMARY,
  secondary: SECONDARY,
  success: { main: '#61C685', contrastText: '#FFF' },
  warning: { main: '#962446' },
  info: { main: '#FFCC37', contrastText: '#3f4244' },
  error: { main: '#FF636D', contrastText: '#FFF' },
  canteen: PRIMARY,
  catering: SECONDARY,
  standingOrder: SECONDARY,
  orderStatus: {
    go: { main: '#63BC6B', contrastText: '#FFFFFF' },
    confirm: { main: '#FFCC37', contrastText: '#5A3A00' },
    cater: { main: '#962446', contrastText: '#FFFFFF' },
    done: { main: '#F1F2F3', contrastText: '#9CA5B0' },
    cancelled: { main: '#FBEAEA', contrastText: '#EA8282' },
  },
  inverted: {
    main: '#FFF',
    dark: '#BDBDBD',
    light: '#FFF',
    contrastText: '#3F4144',
  },
  text: {
    primary: '#3F4144',
    secondary: '#6B8083',
    disabled: '#96A3A5',
    muted: '#727373',
  },
  background: {
    default: '#FAFAFA',
    canvas: '#F4F4F4',
    subtle: '#F1F1F1',
    hover: '#ECEDED',
    border: '#D1D5DB',
  },
  common: { black: '#3F4244' },
}

const { breakpoints } = createTheme()

const createTypography = (palette: Theme['palette']): NonNullable<ThemeOptions['typography']> => ({
  fontFamily: ['var(--montserrat-font, Montserrat)', 'sans-serif'].join(','),
  allVariants: { color: palette.text.primary },
  h0: {
    fontSize: 64,
    lineHeight: 96 / 64,
    fontWeight: 700,
    letterSpacing: -1,
    [breakpoints.up('md')]: {
      fontSize: 96,
      lineHeight: 112 / 96,
    },
  },
  h1: {
    fontSize: 48,
    lineHeight: 64 / 48,
    fontWeight: 700,
    letterSpacing: -1,
    [breakpoints.up('md')]: {
      fontSize: 64,
      lineHeight: 96 / 64,
    },
  },
  h2: {
    fontSize: 32,
    lineHeight: 48 / 32,
    fontWeight: 700,
    letterSpacing: -0.5,
    [breakpoints.up('md')]: {
      fontSize: 48,
      lineHeight: 64 / 48,
    },
  },
  h3: {
    fontSize: 24,
    lineHeight: 32 / 24,
    fontWeight: 700,
    letterSpacing: 0,
    [breakpoints.up('md')]: {
      fontSize: 32,
      lineHeight: 48 / 32,
    },
  },
  h4: {
    fontSize: 18,
    lineHeight: 26 / 18,
    fontWeight: 700,
    letterSpacing: 0,
    [breakpoints.up('md')]: {
      fontSize: 24,
      lineHeight: 32 / 24,
    },
  },
  h5: {
    fontSize: 16,
    lineHeight: 24 / 16,
    fontWeight: 600,
    letterSpacing: 0,
    [breakpoints.up('md')]: {
      fontSize: 18,
      lineHeight: 26 / 18,
    },
  },
  h6: {
    fontSize: 14,
    lineHeight: 20 / 14,
    fontWeight: 700,
    letterSpacing: 0,
    [breakpoints.up('md')]: {
      fontSize: 16,
      lineHeight: 24 / 16,
    },
  },
  subtitle1: {
    fontSize: 18,
    lineHeight: 26 / 18,
    fontWeight: 600,
    letterSpacing: 0,
    [breakpoints.up('md')]: {
      fontSize: 24,
      lineHeight: 32 / 24,
    },
  },
  subtitle2: {
    fontSize: 16,
    lineHeight: 24 / 16,
    fontWeight: 600,
    letterSpacing: 0,
    [breakpoints.up('md')]: {
      fontSize: 18,
      lineHeight: 26 / 18,
    },
  },
  subtitle3: {
    fontSize: 14,
    lineHeight: 22 / 14,
    fontWeight: 600,
    letterSpacing: 0,
    [breakpoints.up('md')]: {
      fontSize: 16,
      lineHeight: 24 / 16,
    },
  },
  body1: {
    fontSize: 16,
    lineHeight: 24 / 16,
    fontWeight: 500,
    letterSpacing: 0,
  },
  body2: {
    fontSize: 14,
    lineHeight: 22 / 14,
    fontWeight: 500,
    letterSpacing: 0,
  },
  body3: {
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 1.2,
    letterSpacing: '0.00938em',
  },
  button: {
    fontSize: 14,
    lineHeight: 24 / 14,
    fontWeight: 600,
    letterSpacing: 0,
    textTransform: 'none',
  },
  caption: {
    fontSize: 12,
    lineHeight: 18 / 12,
    fontWeight: 500,
    letterSpacing: 0,
  },
  overline: {
    fontSize: 10,
    lineHeight: 12 / 10,
    fontWeight: 600,
    letterSpacing: 0,
  },
})

const CONTAINER_GUTTERS = {
  xs: 2,
  sm: 3,
  md: 4,
  lg: 10,
  xl: 22,
} as const

const ALERT_SEVERITIES = ['success', 'info', 'warning', 'error'] as const

const createComponents = (): Components<Omit<Theme, 'components'>> => ({
  MuiAccordion: {
    defaultProps: {
      disableGutters: true,
    },
    styleOverrides: {
      root: {
        [`&.${accordionClasses.expanded}:before`]: {
          opacity: 1,
        },
      },
    },
    variants: [
      {
        props: { variant: 'sidebar' },
        style: ({ theme }) => ({
          borderRadius: 4,
          '&:before': { display: 'none' },
          backgroundColor: 'transparent',
          border: theme.borders?.light ?? `1px solid ${alpha(theme.palette.divider, 0.4)}`,
          boxShadow: 'none',
          transition: 'box-shadow 0.2s ease-in-out',
          '&:first-of-type': {
            borderRadius: 4,
          },
          '&:last-of-type': {
            borderRadius: 4,
          },
          '&:last-child': {
            borderRadius: 4,
            '&:not(.Mui-expanded)': {
              borderRadius: 4,
            },
            '&.Mui-expanded': {
              borderRadius: 4,
            },
          },
        }),
      },
    ],
  },
  MuiAccordionSummary: {
    variants: [
      {
        props: { variant: 'sidebar' },
        style: ({ theme }) => ({
          borderRadius: 4,
          backgroundColor: alpha(theme.palette.primary.main, 0.3),
          '.MuiAccordionSummary-content': { margin: 0 },
          '&.Mui-expanded': {
            borderBottom: theme.borders?.light ?? `1px solid ${theme.palette.divider}`,
            borderBottomRightRadius: 0,
            borderBottomLeftRadius: 0,
          },
        }),
      },
    ],
  },
  MuiAlert: {
    defaultProps: {
      iconMapping: {
        success: <CheckCircleRounded fontSize="inherit" />,
        info: <InfoRounded fontSize="inherit" />,
        warning: <WarningRounded fontSize="inherit" />,
        error: <ErrorRounded fontSize="inherit" />,
      },
    },
    styleOverrides: {
      filled: ({ ownerState, theme }) => {
        const severity = (ownerState.severity ?? 'success') as (typeof ALERT_SEVERITIES)[number]
        const palette =
          (theme.palette[severity] as PaletteColor | undefined) ?? theme.palette.success
        return { color: palette.contrastText ?? theme.palette.common.white }
      },
    },
  },
  MuiAlertTitle: {
    defaultProps: {
      color: 'inherit',
    },
  },
  MuiAutocomplete: {
    defaultProps: {
      popupIcon: <KeyboardArrowDownRounded />,
    },
  },
  MuiButton: {
    defaultProps: {
      disableElevation: true,
    },
    styleOverrides: {
      root: {
        borderRadius: 100,
      },
    },
  },
  MuiButtonGroup: {
    styleOverrides: {
      root: {
        borderRadius: 100,
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        boxShadow: 'rgba(0, 0, 0, 0.04) 0px 5px 22px 0px, rgba(0, 0, 0, 0.06) 0px 0px 0px 1px',
      },
    },
  },
  MuiCardHeader: {
    styleOverrides: {
      root: {
        padding: '32px 24px 16px',
      },
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        '--Grid-borderWidth': '1px',
        borderColor: 'rgba(0, 0, 0, 0.12)',
        '& .MuiStack-root > .MuiGrid-container': {
          borderColor: 'rgba(0, 0, 0, 0.12)',
          '& .MuiGrid-root': {
            borderColor: 'rgba(0, 0, 0, 0.12)',
          },
          '& .MuiGrid-root:not(:first-child)': {
            borderLeft: 'var(--Grid-borderWidth) solid',
            borderColor: 'rgba(0, 0, 0, 0.12)',
          },
        },
      },
    },
  },
  MuiCardActions: {
    styleOverrides: {
      root: {
        padding: '16px 24px 32px',
      },
    },
  },
  MuiContainer: {
    defaultProps: {
      maxWidth: 'xl',
    },
    styleOverrides: {
      root: ({ ownerState, theme }) => {
        if (ownerState.disableGutters) return {}

        return Object.entries(CONTAINER_GUTTERS).reduce<Record<string, unknown>>(
          (acc, [breakpoint, value]) => ({
            ...acc,
            [theme.breakpoints.up(breakpoint as keyof typeof CONTAINER_GUTTERS)]: {
              paddingLeft: theme.spacing(value),
              paddingRight: theme.spacing(value),
            },
          }),
          {},
        )
      },
    },
  },
  MuiDataGrid: {
    defaultProps: {
      autoHeight: true,
      initialState: {
        pagination: {
          paginationModel: {
            pageSize: 100,
          },
        },
      },
    },
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.background.paper,
        '& .MuiDataGrid-columnHeaderTitle': {
          textTransform: 'uppercase',
        },
        '& .MuiDataGrid-row': {
          transition: 'background-color 200ms ease-in-out',
          '&:hover, &.Mui-hovered': {
            backgroundColor: alpha(theme.palette.primary.light ?? theme.palette.primary.main, 0.5),
            '@media (hover: none)': {
              backgroundColor: 'transparent',
            },
          },
        },
      }),
    },
  },
  MuiTablePagination: {
    styleOverrides: {
      root: ({ theme }) => ({
        [`& .${tablePaginationClasses.actions} .${iconButtonClasses.root}:not(.${iconButtonClasses.disabled})`]:
          {
            color: theme.palette.action.active,
          },
      }),
    },
  },
  MuiDatePicker: {
    defaultProps: {
      enableAccessibleFieldDOMStructure: false,
      slots: {
        openPickerIcon: EventRounded,
        switchViewIcon: KeyboardArrowDownRounded,
      },
    },
  },
  MuiDateTimePicker: {
    defaultProps: {
      enableAccessibleFieldDOMStructure: false,
      slots: {
        openPickerIcon: EventRounded,
      },
    },
  },
  MuiTimePicker: {
    defaultProps: {
      enableAccessibleFieldDOMStructure: false,
    },
  },
  MuiTimeField: {
    defaultProps: {
      enableAccessibleFieldDOMStructure: false,
    },
  },
  MuiDateCalendar: {
    defaultProps: {
      slots: {
        switchViewIcon: KeyboardArrowDownRounded,
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      root: {
        '& .MuiPaper-root': {
          borderRadius: '8px',
        },
      },
    },
  },
  MuiDialogContent: {
    defaultProps: {
      dividers: true,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        padding: `${theme.spacing(1)} ${theme.spacing(3)}`,
      }),
    },
  },
  MuiDialogContentText: {
    defaultProps: {
      color: 'textPrimary',
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
      }),
    },
  },
  MuiLink: {
    defaultProps: {
      underline: 'hover',
    },
    styleOverrides: {
      underlineHover: {
        position: 'relative',
        '&::after': {
          transition: 'transform 200ms ease',
          content: '""',
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: '0.07em',
          height: '0.07em',
          transform: 'scaleX(0)',
          backgroundColor: 'currentColor',
        },
        '&:hover': {
          textDecoration: 'none',
          '&::after': {
            transform: 'scaleX(1)',
          },
        },
      },
    },
  },
  MuiSelect: {
    defaultProps: {
      variant: 'outlined',
      IconComponent: KeyboardArrowDownRounded,
    },
  },
  MuiTextField: {
    variants: [
      {
        props: { variant: 'rounded' as any },
        style: {
          [`& .${inputBaseClasses.root}`]: {
            borderRadius: '100px',
          },
        },
      },
    ],
  },
  MuiToggleButton: {
    styleOverrides: {
      root: {
        [`&.${toggleButtonClasses.selected}`]: {
          borderColor: 'currentColor',
        },
      },
    },
  },
  MuiTooltip: {
    defaultProps: {
      arrow: true,
    },
    styleOverrides: {
      tooltip: ({ theme }) => ({
        backgroundColor: theme.palette.text.primary,
      }),
      arrow: ({ theme }) => ({
        color: theme.palette.text.primary,
      }),
    },
  },
  MuiStepIcon: {
    styleOverrides: {
      root: ({ theme }) => ({
        [`&:not(.${stepIconClasses.completed}):not(.${stepIconClasses.error})`]: {
          borderRadius: theme.spacing(8),
          color: alpha(theme.palette.text.primary, 0.04),
          border: `1px solid ${theme.palette.text.disabled}`,
          [`&.${stepIconClasses.active}`]: {
            color: 'transparent',
            borderColor: theme.palette.success.main,
            [`& .${stepIconClasses.text}`]: {
              fill: theme.palette.success.main,
            },
          },
          [`&.${stepIconClasses.completed}`]: {
            border: 'none',
          },
        },
      }),
      text: ({ theme }) => ({
        fill: theme.palette.text.primary,
      }),
    },
  },
  MuiStepLabel: {
    styleOverrides: {
      label: ({ theme }) => ({
        fontSize: theme.typography.subtitle2?.fontSize,
        fontWeight: theme.typography.subtitle2?.fontWeight,
        [`&.${stepLabelClasses.active}:not(.${stepLabelClasses.error})`]: {
          color: theme.palette.success.main,
        },
        [`&.${stepLabelClasses.disabled}`]: {
          color: theme.palette.text.disabled,
        },
      }),
    },
  },
  MuiTypography: {
    defaultProps: {
      variantMapping: {
        h0: 'h1',
        subtitle3: 'h6',
      },
    },
  },
})

const buildTheme = (palette: PaletteOptions) => {
  const base = createTheme({ palette })

  const theme = createTheme({
    palette,
    typography: createTypography(base.palette),
    components: createComponents(),
    shape: {
      borderRadiusChip: 10,
    },
    surfaceShadows: {
      resting: '0 1px 2px rgba(20,23,26,.05),0 1px 2px rgba(20,23,26,.03)',
      hover: '0 6px 20px rgba(20,23,26,.09)',
      overlay: '0 18px 46px rgba(20,23,26,.16)',
    },
    colors: legacyTheme.colors,
    fontSizes: legacyTheme.fontSizes,
    fontWeights: legacyTheme.fontWeights,
    fontFaces: legacyTheme.fontFaces,
    spacings: legacyTheme.spacings,
  })

  return theme
}

export const muiTheme = buildTheme(DEFAULT_PALETTE)

export default muiTheme
